import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Share2, Clock, User, UserPlus, Users, XCircle,
         MessageCircle, Video, UtensilsCrossed, Mountain, Compass, Gamepad2, Coffee, Shuffle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchSelect } from "@/components/SearchSelect";
import { COUNTRIES, countryByIso } from "@/lib/countries";

const dialOptions = COUNTRIES.map((c) => ({ id: c.iso, search: c.search, ...c }));

const QUESTIONS = [
  { key: "q1", type: "cards_single", title: "Your flight leaves tomorrow. What are you most excited about?",
    options: [
      { label: "Adventure & nature", img: "/images/cards/red-rock-reset.webp" },
      { label: "Beach & relaxation", img: "/images/cards/san-diego-surf-days.webp" },
      { label: "Exploring a new city", img: "/images/cards/nyc-takeover.webp" },
      { label: "Food & culture", img: "/images/cards/food-culture.webp" },
      { label: "Meeting people & nightlife", img: "/images/cards/miami-vice-weekend.webp" },
      { label: "Honestly... all of it", img: "/images/cards/hero.webp" },
    ] },
  { key: "q2", type: "single", title: "What is your age range?",
    options: ["18–24", "25–29", "30–34", "35–39", "40–45", "Outside 18–45"] },
  { key: "q3", type: "single", title: "How often do you travel for leisure?",
    options: ["Rarely", "1–2 times a year", "3–4 times a year", "5+ times a year"] },
  { key: "q4", type: "multi", max: 99, title: "Who do you usually travel with?", hint: "Select all that apply.",
    options: ["Solo", "Friends", "Partner", "Family", "Organized groups", "It varies"] },
  { key: "q5", type: "icons", multi: true, max: 99, title: "Your friends cancel the trip... again. What do you usually do?", hint: "Select all that apply.",
    options: [
      { label: "Wait until everyone is free", icon: Clock },
      { label: "Go solo anyway", icon: User },
      { label: "Find someone else to come", icon: UserPlus },
      { label: "I'd consider joining a group trip", icon: Users },
      { label: "Usually cancel/postpone the trip", icon: XCircle },
    ] },
  { key: "q6", type: "multi", max: 99, title: "What usually stops you from traveling more?", hint: "Select all that apply.",
    options: ["Cost", "Friends aren't available", "Planning takes too much work", "I don't want to travel alone", "Work or school", "Safety concerns", "Finding the right destination", "Not enough time", "Other"] },
  { key: "q7", type: "cards", max: 3, title: "Which experiences interest you most?", hint: "Pick up to 3",
    options: [
      { label: "Mountains & nature", img: "/images/cards/rocky-mountain-high.webp" },
      { label: "Beach & tropical", img: "/images/cards/san-diego-surf-days.webp" },
      { label: "City weekends", img: "/images/cards/nyc-takeover.webp" },
      { label: "Cruises", img: "/images/cards/roamies-bahamas-cruise.webp" },
      { label: "National parks", img: "/images/cards/yellowstone-adventure.webp" },
      { label: "Road trips", img: "/images/cards/hero.webp" },
      { label: "International adventures", img: "/images/cards/tokyo-drift.webp" },
      { label: "Adventure activities", img: "/images/cards/red-rock-reset.webp" },
      { label: "Relaxing/social getaways", img: "/images/cards/social-2.webp" },
    ] },
  { key: "q8", type: "single", title: "How long is your ideal getaway?",
    options: ["Weekend — 2–3 days", "4–5 days", "6–7 days", "8–10 days", "10+ days", "Depends on the destination"] },
  { key: "q9", type: "single", title: "For a 3–5 day U.S. group trip, what total trip price would feel reasonable?",
    options: ["Under $500", "$500–$749", "$750–$999", "$1,000–$1,499", "$1,500+", "Depends on what's included"] },
  { key: "q10", type: "scale", title: "How comfortable would you be joining a small-group trip where you don't already know the other travelers?",
    scaleLabels: ["Not comfortable", "A little unsure", "I'd consider it", "Comfortable", "Absolutely, let's go!"] },
  { key: "q11", type: "multi", max: 99, title: "What would make you feel more comfortable traveling with people you haven't met before?", hint: "Pick all that apply",
    options: ["Small group size", "Pre-trip group chat", "Pre-trip video meetup", "Clear itinerary before booking", "Verified/trusted organizer", "Traveler introductions/profiles", "Clear safety information", "Reviews from previous travelers", "Other"] },
   { key: "q12", type: "icons", multi: true, max: 99, title: "What would help turn a group of new people into a real crew?", hint: "Select all that apply.",
    options: [
      { label: "Pre-trip group chat", icon: MessageCircle },
      { label: "Video meetup before the trip", icon: Video },
      { label: "Welcome dinner", icon: UtensilsCrossed },
      { label: "Group activities", icon: Mountain },
      { label: "Shared adventures", icon: Compass },
      { label: "Games/icebreakers", icon: Gamepad2 },
      { label: "Enough free time to connect naturally", icon: Coffee },
      { label: "A mixture of these", icon: Shuffle },
    ] },
  { key: "q13", type: "multi", max: 99, title: "What matters MOST when deciding whether you'd book with ROAMIES?", hint: "Select all that apply.",
    options: ["Price", "Quality accommodation", "Well-planned itinerary", "Meeting new people", "Small group size", "Safety & trust", "Unique experiences", "Flexible payment options", "Transportation arrangements", "Reviews/reputation"] },
  { key: "q14", type: "text", title: "What would make a ROAMIES trip worth choosing instead of planning the same trip yourself?",
    hint: "Tell us what would make you say: “Yeah, I'd rather do this with ROAMIES.”", placeholder: "Type your answer..." },
  { key: "q15", type: "text", title: "Dream a little... Where would you love ROAMIES to take you?", placeholder: "A place, a vibe, a bucket-list moment..." },
  { key: "q16", type: "personality", title: "Which traveler sounds most like you?",
    options: [
      { value: "adventurer", label: "The Adventurer", sub: "Give me something exciting.", img: "/images/cards/rocky-mountain-high.webp" },
      { value: "social_explorer", label: "The Social Explorer", sub: "The people are part of the experience.", img: "/images/cards/social-1.webp" },
      { value: "culture_chaser", label: "The Culture Chaser", sub: "Food, culture and local experiences.", img: "/images/cards/tokyo-drift.webp" },
      { value: "chill_roamer", label: "The Chill Roamer", sub: "Relaxation with some exploring.", img: "/images/cards/hawaii-island-hop.webp" },
      { value: "memory_maker", label: "The Memory Maker", sub: "I'm here for unforgettable moments.", img: "/images/cards/social-3.webp" },
      { value: "spontaneous_roamer", label: "The Spontaneous Roamer", sub: "Tell me where we're going and I'm ready.", img: "/images/cards/hero.webp" },
    ] },
];

const COMM_OPTIONS = [
  { value: "marketing", title: "Yes, keep me in the loop ✈️", sub: "I'd like to receive ROAMIES trip announcements, destinations and other updates." },
  { value: "follow_up_only", title: "Survey follow-up only 💬", sub: "ROAMIES may contact me specifically regarding my survey response, but not for general marketing." },
  { value: "none", title: "No thanks 😊", sub: "Please don't send me promotional or marketing communications." },
];

const TOTAL_STEPS = QUESTIONS.length + 2; // 16 questions + contact + comm preference

// graceful image: never show a broken-image icon — fall back to a branded solid card
const SurveyImg = ({ src, alt, className }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className={`bg-ink ${className}`} aria-hidden="true" />;
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={className} />;
};

const slide = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.3, ease: [0.215, 0.61, 0.355, 1] },
};

export default function SurveyPage() {
  useSEO("ROAMIES Travel Survey | Discover Your Travel Style",
    "Help shape future ROAMIES adventures. Tell us how you love to travel and discover your travel style.",
    { image: `${window.location.origin}/images/hero.jpg` });
  const [step, setStep] = useState(0); // 0 = intro, 1..16 questions, 17 contact, 18 comm preference
  const [answers, setAnswers] = useState({});
  const [submissionId] = useState(() => crypto.randomUUID()); // idempotency key — safe retries/refresh never duplicate
  const [contact, setContact] = useState({ full_name: "", email: "", mobile: "", mobile_iso: "US", instagram: "", tiktok: "" });
  const [noInsta, setNoInsta] = useState(false);
  const [noTiktok, setNoTiktok] = useState(false);
  const [commPref, setCommPref] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const progress = Math.min(step / TOTAL_STEPS, 1);
  const q = step >= 1 && step <= QUESTIONS.length ? QUESTIONS[step - 1] : null;

  // quietly preload the NEXT question's card images while the user answers the current one
  useEffect(() => {
    const next = step >= 1 && step < QUESTIONS.length ? QUESTIONS[step] : null;
    if (!next) return;
    (next.options || []).forEach((o) => {
      if (o && o.img) {
        const img = new Image();
        img.src = o.img;
      }
    });
  }, [step]);

  const answered = useMemo(() => {
    if (!q) return false;
    const v = answers[q.key];
    if (q.type === "multi" || q.type === "cards") return Array.isArray(v) && v.length > 0;
    if (q.type === "text") return typeof v === "string" && v.trim().length > 0;
    return v !== undefined && v !== "";
  }, [q, answers]);

  const chooseSingle = (val) => {
    setAnswers((a) => ({ ...a, [q.key]: val }));
    setTimeout(() => setStep((s) => s + 1), 280);
  };

  const toggleMulti = (label) => {
    const cur = Array.isArray(answers[q.key]) ? answers[q.key] : [];
    if (cur.includes(label)) {
      setAnswers((a) => ({ ...a, [q.key]: cur.filter((x) => x !== label) }));
    } else if (cur.length < q.max) {
      setAnswers((a) => ({ ...a, [q.key]: [...cur, label] }));
    }
  };

  const mobileDigits = contact.mobile.replace(/\D/g, "");
  const [contactErrors, setContactErrors] = useState({});

  const setContactField = (key, value) => {
    setContact((c) => ({ ...c, [key]: value }));
    setContactErrors((er) => ({ ...er, [key]: "" }));
  };

  const contactContinue = () => {
    const errs = {};
    if (!contact.full_name.trim()) errs.full_name = "Please enter your full name.";
    if (!/\S+@\S+\.\S+/.test(contact.email.trim())) errs.email = "Please enter a valid email address.";
    if (!mobileDigits.length) errs.mobile = "Please enter your mobile number.";
    setContactErrors(errs);
    if (Object.keys(errs).length) {
      const first = ["full_name", "email", "mobile"].find((k) => errs[k]);
      document.getElementById(`sv-${first === "full_name" ? "name" : first}`)?.focus();
      return;
    }
    setStep(18);
  };

  const submit = async () => {
    if (!commPref) {
      toast.error("Please choose a communication preference.");
      return;
    }
    setSubmitting(true);
    try {
      const dial = (countryByIso(contact.mobile_iso)?.dial || "+1").replace(/\D/g, "");
      const { data } = await api.post("/survey", {
        submission_id: submissionId,
        full_name: contact.full_name.trim(),
        email: contact.email.trim(),
        mobile: `+${dial}${mobileDigits.replace(/^0+/, "")}`,
        instagram: noInsta ? "I don't use Instagram" : contact.instagram.trim(),
        tiktok: noTiktok ? "I don't use TikTok" : contact.tiktok.trim(),
        comm_preference: commPref,
        answers,
      });
      setResult(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const share = async () => {
    const text = `My Roamies travel style is ${result?.personality}! Take the survey:`;
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: "Roamies Travel Style", text, url }); } catch { /* dismissed */ }
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied — share your travel style anywhere.");
    }
  };

  const optCls = (selected) =>
    `w-full text-left border px-5 py-4 font-semibold transition-colors flex items-center justify-between gap-3 ${
      selected ? "bg-ink text-cream border-ink" : "bg-white border-ink/15 hover:border-flame"}`;

  return (
    <div className="min-h-screen bg-cream" data-testid="survey-page">
      {/* progress */}
      {step > 0 && !result && (
        <div className="sticky top-16 z-40 bg-cream/90 backdrop-blur border-b border-ink/10" data-testid="survey-progress">
          <div className="h-1 bg-ink/10">
            <div className="h-full bg-flame transition-all duration-300" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="survey-back-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-flame transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <p className="text-xs font-bold uppercase tracking-widest text-ink/50" data-testid="survey-step-label">
              {step <= QUESTIONS.length ? `Question ${step} of ${QUESTIONS.length}` : step === 17 ? "About you" : "Your choice"}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <AnimatePresence mode="wait">

          {/* INTRO */}
          {step === 0 && !result && (
            <motion.div key="intro" {...slide} data-testid="survey-intro">
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-flame mb-5">Roamies Survey</p>
              <h1 className="font-display text-4xl sm:text-6xl uppercase leading-[0.92]" data-testid="survey-intro-title">
                Let's find your travel personality ✈️
              </h1>
              <p className="mt-6 text-base sm:text-lg text-ink/70 leading-relaxed">
                We're building ROAMIES around real travelers, not assumptions. Tell us what makes you want to pack
                your bags, what holds you back, and what would make your next group adventure unforgettable.
              </p>
              <p className="mt-4 text-sm font-bold uppercase tracking-widest text-ink/50">
                2–3 minutes • Your answers help shape future ROAMIES adventures.
              </p>
              <p className="mt-1 text-sm text-flame font-semibold">Show up solo. Leave with a family.</p>
              <Button onClick={() => setStep(1)} data-testid="survey-start-btn"
                className="mt-10 bg-flame hover:bg-flame/90 text-white rounded-none h-14 px-10 font-bold uppercase tracking-[0.2em] text-sm transition-transform hover:-translate-y-0.5">
                Let's Roam <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {/* QUESTIONS */}
          {q && !result && (
            <motion.div key={q.key} {...slide} data-testid="survey-question-step">
              <h2 className="font-display text-3xl sm:text-4xl uppercase leading-[1.02]" data-testid="survey-question-title">{q.title}</h2>
              {q.hint && <p className="mt-3 text-xs font-bold uppercase tracking-widest text-flame">{q.hint}</p>}

              {q.type === "single" && (
                <div className="mt-8 space-y-3">
                  {q.options.map((o, i) => {
                    const val = typeof o === "string" ? o : o.value;
                    const label = typeof o === "string" ? o : o.label;
                    const sub = typeof o === "string" ? null : o.sub;
                    const selected = answers[q.key] === val;
                    return (
                      <button key={val} onClick={() => chooseSingle(val)} data-testid={`survey-option-${q.key}-${i}`}
                        className={optCls(selected)}>
                        <span>{label}{sub && <span className="block text-xs font-normal opacity-60 mt-0.5">{sub}</span>}</span>
                        {selected && <Check className="w-5 h-5 text-flame shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* visual single-select cards (q1) */}
              {q.type === "cards_single" && (
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {q.options.map((o, i) => {
                    const selected = answers[q.key] === o.label;
                    return (
                      <button key={o.label} onClick={() => chooseSingle(o.label)} data-testid={`survey-option-${q.key}-${i}`}
                        className={`relative overflow-hidden aspect-[4/3] text-left border-2 transition-colors ${selected ? "border-flame" : "border-transparent"}`}>
                        <SurveyImg src={o.img} alt={o.label} className="absolute inset-0 w-full h-full object-cover" />
                        <div className={`absolute inset-0 transition-colors ${selected ? "bg-ink/30" : "bg-ink/55 hover:bg-ink/40"}`} />
                        <span className="absolute bottom-0 left-0 right-0 p-2.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider text-white leading-tight">{o.label}</span>
                        {selected && <span className="absolute top-2 right-2 bg-flame text-white rounded-full p-1"><Check className="w-3.5 h-3.5" /></span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* playful icon cards (q5, q12) */}
              {q.type === "icons" && (
                <>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {q.options.map((o, i) => {
                      const Icon = o.icon;
                      const selected = q.multi
                        ? (answers[q.key] || []).includes(o.label)
                        : answers[q.key] === o.label;
                      return (
                        <button key={o.label} onClick={() => (q.multi ? toggleMulti(o.label) : chooseSingle(o.label))} data-testid={`survey-option-${q.key}-${i}`}
                          className={`border p-5 text-left transition-colors flex items-start gap-4 ${
                            selected ? "bg-ink text-cream border-ink" : "bg-white border-ink/15 hover:border-flame"}`}>
                          <span className={`shrink-0 w-10 h-10 flex items-center justify-center border ${selected ? "border-flame text-flame" : "border-ink/20 text-flame"}`}>
                            {selected ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                          </span>
                          <span className="font-semibold text-sm leading-snug pt-1">{o.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {q.multi && (
                    <Button onClick={() => setStep((s) => s + 1)} disabled={!answered} data-testid="survey-next-btn"
                      className="mt-8 bg-ink hover:bg-ink/90 text-cream rounded-none h-12 px-8 font-bold uppercase tracking-widest text-xs disabled:opacity-40">
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </>
              )}

              {/* premium personality cards (q16) */}
              {q.type === "personality" && (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {q.options.map((o, i) => {
                    const selected = answers[q.key] === o.value;
                    return (
                      <button key={o.value} onClick={() => chooseSingle(o.value)} data-testid={`survey-option-${q.key}-${i}`}
                        className={`relative overflow-hidden text-left border-2 transition-colors group ${selected ? "border-flame" : "border-transparent"}`}>
                        <div className="relative h-36 overflow-hidden">
                          <SurveyImg src={o.img} alt={o.label}
                            className="absolute inset-0 w-full h-full object-cover saturate-[.9] transition-transform duration-500 group-hover:scale-105" />
                          <div className={`absolute inset-0 transition-colors ${selected ? "bg-ink/25" : "bg-ink/45 group-hover:bg-ink/35"}`} />
                          {selected && <span className="absolute top-2.5 right-2.5 bg-flame text-white rounded-full p-1"><Check className="w-4 h-4" /></span>}
                        </div>
                        <div className={`p-4 transition-colors ${selected ? "bg-ink text-cream" : "bg-white"}`}>
                          <p className="font-display text-xl uppercase leading-none">{o.label}</p>
                          <p className={`text-xs mt-1.5 ${selected ? "text-cream/70" : "text-ink/55"}`}>{o.sub}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {(q.type === "multi" || q.type === "cards") && (
                <>
                  {q.type === "cards" ? (
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {q.options.map((o, i) => {
                        const selected = (answers[q.key] || []).includes(o.label);
                        return (
                          <button key={o.label} onClick={() => toggleMulti(o.label)} data-testid={`survey-option-${q.key}-${i}`}
                            className={`relative overflow-hidden aspect-[4/3] text-left border-2 transition-all ${selected ? "border-flame ring-2 ring-flame/40" : "border-transparent"}`}>
                            <SurveyImg src={o.img} alt={o.label} className="absolute inset-0 w-full h-full object-cover" />
                            <div className={`absolute inset-0 transition-colors ${selected ? "bg-ink/25" : "bg-ink/55 hover:bg-ink/40"}`} />
                            <span className="absolute bottom-0 left-0 right-0 p-2.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider text-white leading-tight">{o.label}</span>
                            {selected && <span className="absolute top-2 right-2 bg-flame text-white rounded-full p-1 shadow-lg"><Check className="w-3.5 h-3.5" /></span>}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-8 space-y-3">
                      {q.options.map((o, i) => {
                        const selected = (answers[q.key] || []).includes(o);
                        return (
                          <button key={o} onClick={() => toggleMulti(o)} data-testid={`survey-option-${q.key}-${i}`} className={optCls(selected)}>
                            <span>{o}</span>
                            {selected && <Check className="w-5 h-5 text-flame shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <Button onClick={() => setStep((s) => s + 1)} disabled={!answered} data-testid="survey-next-btn"
                    className="mt-8 bg-ink hover:bg-ink/90 text-cream rounded-none h-12 px-8 font-bold uppercase tracking-widest text-xs disabled:opacity-40">
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}

              {q.type === "scale" && (
                <div className="mt-8">
                  <div className="grid grid-cols-5 gap-2 sm:gap-3">
                    {q.scaleLabels.map((label, i) => {
                      const selected = answers[q.key] === i + 1;
                      return (
                        <button key={i} onClick={() => chooseSingle(i + 1)} data-testid={`survey-option-${q.key}-${i + 1}`} className="text-center">
                          <span className={`block h-16 sm:h-20 border-2 font-display text-3xl leading-[4rem] sm:leading-[5rem] transition-all ${
                            selected ? "bg-flame text-white border-flame scale-105 shadow-[0_8px_24px_rgba(255,69,0,0.35)]" : "bg-white border-ink/15 hover:border-flame"}`}>{i + 1}</span>
                          <span className={`block mt-2 text-[10px] sm:text-xs font-semibold leading-tight ${selected ? "text-flame" : "text-ink/60"}`}>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {q.type === "text" && (
                <div className="mt-8">
                  <Textarea value={answers[q.key] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                    placeholder={q.placeholder} data-testid={`survey-text-${q.key}`}
                    className="rounded-none min-h-[110px] border-ink/20 focus-visible:ring-flame bg-white" />
                  <Button onClick={() => setStep((s) => s + 1)} disabled={!answered} data-testid="survey-next-btn"
                    className="mt-6 bg-ink hover:bg-ink/90 text-cream rounded-none h-12 px-8 font-bold uppercase tracking-widest text-xs disabled:opacity-40">
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* CONTACT */}
          {step === 17 && !result && (
            <motion.div key="contact" {...slide} data-testid="survey-contact-step">
              <h2 className="font-display text-3xl sm:text-4xl uppercase leading-[1.02]">Almost there — tell us who's roaming</h2>
              <p className="mt-4 text-sm text-ink/60 leading-relaxed">
                We'd love to know a little about the traveler behind the answers. Your contact information helps us keep
                each survey response connected to the right person and better understand our ROAMIES community.
              </p>
              <div className="mt-8 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="sv-name" className="text-xs font-bold uppercase tracking-widest">Full Name <span className="text-flame">*</span></Label>
                  <Input id="sv-name" value={contact.full_name} placeholder="Your name"
                    onChange={(e) => setContactField("full_name", e.target.value)}
                    data-testid="survey-contact-full-name"
                    className={`rounded-none h-12 bg-white border-ink/20 focus-visible:ring-flame ${contactErrors.full_name ? "border-red-500" : ""}`} />
                  {contactErrors.full_name && <p className="text-xs font-semibold text-red-600" data-testid="survey-contact-name-error">{contactErrors.full_name}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sv-email" className="text-xs font-bold uppercase tracking-widest">Email Address <span className="text-flame">*</span></Label>
                  <Input id="sv-email" type="email" value={contact.email} placeholder="you@email.com"
                    onChange={(e) => setContactField("email", e.target.value)}
                    data-testid="survey-contact-email"
                    className={`rounded-none h-12 bg-white border-ink/20 focus-visible:ring-flame ${contactErrors.email ? "border-red-500" : ""}`} />
                  {contactErrors.email && <p className="text-xs font-semibold text-red-600" data-testid="survey-contact-email-error">{contactErrors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest">Mobile Number <span className="text-flame">*</span></Label>
                  <div className="flex gap-2">
                    <div className="w-36 sm:w-44 shrink-0">
                      <SearchSelect selectedIso={contact.mobile_iso} onSelect={(v) => setContact({ ...contact, mobile_iso: v })}
                        placeholder="Code" testid="survey-contact-mobile-code" options={dialOptions}
                        renderSelected={(o) => <span>{o.iso} {o.dial}</span>}
                        renderOption={(o) => <span className="flex justify-between w-full gap-2"><span className="truncate">{o.name}</span><span className="text-ink/40 shrink-0">{o.dial}</span></span>} />
                    </div>
                    <Input id="sv-mobile" type="tel" inputMode="tel" value={contact.mobile} placeholder="555 123 4567"
                      onChange={(e) => setContactField("mobile", e.target.value)}
                      data-testid="survey-contact-mobile"
                      className={`rounded-none h-12 bg-white border-ink/20 focus-visible:ring-flame flex-1 ${contactErrors.mobile ? "border-red-500" : ""}`} />
                  </div>
                  {contactErrors.mobile && <p className="text-xs font-semibold text-red-600" data-testid="survey-contact-mobile-error">{contactErrors.mobile}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sv-insta" className="text-xs font-bold uppercase tracking-widest">Instagram <span className="text-ink/40 normal-case font-medium">(optional)</span></Label>
                  <Input id="sv-insta" value={contact.instagram} placeholder="@username (optional)" disabled={noInsta}
                    onChange={(e) => setContact({ ...contact, instagram: e.target.value })}
                    data-testid="survey-contact-instagram"
                    className="rounded-none h-12 bg-white border-ink/20 focus-visible:ring-flame disabled:opacity-40" />
                  <label className="flex items-center gap-2.5 text-sm font-semibold cursor-pointer text-ink/70">
                    <Checkbox checked={noInsta} onCheckedChange={(v) => { setNoInsta(!!v); if (v) setContact((c) => ({ ...c, instagram: "" })); }}
                      data-testid="survey-contact-no-instagram" className="rounded-none data-[state=checked]:bg-flame data-[state=checked]:border-flame border-ink/30" />
                    I don't use Instagram
                  </label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sv-tiktok" className="text-xs font-bold uppercase tracking-widest">TikTok <span className="text-ink/40 normal-case font-medium">(optional)</span></Label>
                  <Input id="sv-tiktok" value={contact.tiktok} placeholder="@username (optional)" disabled={noTiktok}
                    onChange={(e) => setContact({ ...contact, tiktok: e.target.value })}
                    data-testid="survey-contact-tiktok"
                    className="rounded-none h-12 bg-white border-ink/20 focus-visible:ring-flame disabled:opacity-40" />
                  <label className="flex items-center gap-2.5 text-sm font-semibold cursor-pointer text-ink/70">
                    <Checkbox checked={noTiktok} onCheckedChange={(v) => { setNoTiktok(!!v); if (v) setContact((c) => ({ ...c, tiktok: "" })); }}
                      data-testid="survey-contact-no-tiktok" className="rounded-none data-[state=checked]:bg-flame data-[state=checked]:border-flame border-ink/30" />
                    I don't use TikTok
                  </label>
                </div>
              </div>
              <Button onClick={contactContinue} data-testid="survey-contact-continue-btn"
                className="mt-8 bg-ink hover:bg-ink/90 text-cream rounded-none h-12 px-8 font-bold uppercase tracking-widest text-xs">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {/* COMMUNICATION PREFERENCE */}
          {step === 18 && !result && (
            <motion.div key="comm" {...slide} data-testid="survey-comm-step">
              <h2 className="font-display text-3xl sm:text-4xl uppercase leading-[1.02]">Your information, your choice</h2>
              <p className="mt-4 text-sm text-ink/60 leading-relaxed">
                Your information and survey responses will help ROAMIES understand our travelers and improve future
                experiences. Providing your contact details does not automatically sign you up for promotional messages.
              </p>
              <p className="mt-6 text-xs font-bold uppercase tracking-widest">Would you like to hear from ROAMIES after completing the survey? <span className="text-flame">*</span></p>
              <div className="mt-4 space-y-3">
                {COMM_OPTIONS.map((o) => {
                  const selected = commPref === o.value;
                  return (
                    <button key={o.value} onClick={() => setCommPref(o.value)} data-testid={`survey-comm-${o.value}`}
                      className={optCls(selected)}>
                      <span>{o.title}<span className="block text-xs font-normal opacity-60 mt-1">{o.sub}</span></span>
                      {selected && <Check className="w-5 h-5 text-flame shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <p className="mt-6 text-xs text-ink/50 leading-relaxed border-l-2 border-flame pl-3" data-testid="survey-privacy-note">
                Your survey responses and contact information will be used to understand traveler interests and improve
                ROAMIES. Marketing communication will only be sent according to the communication preference you select above.
              </p>
              <Button onClick={submit} disabled={!commPref || submitting} data-testid="survey-submit-btn"
                className="mt-8 bg-flame hover:bg-flame/90 text-white rounded-none h-14 px-10 font-bold uppercase tracking-[0.2em] text-sm disabled:opacity-40 transition-transform hover:-translate-y-0.5">
                {submitting ? "Submitting..." : <>Discover My Travel Style <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </motion.div>
          )}

          {/* RESULT */}
          {result && (
            <motion.div key="result" {...slide} className="text-center" data-testid="survey-result">
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-ink/50">Your Roamies travel style is...</p>
              <h1 className="mt-4 font-display text-5xl sm:text-7xl uppercase leading-[0.9] text-ink" data-testid="survey-result-personality">
                {result.personality}<span className="text-flame">.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-ink/70 leading-relaxed max-w-md mx-auto" data-testid="survey-result-description">
                {result.description}
              </p>
              <div className="mt-10 border-t border-ink/10 pt-8">
                <p className="text-sm text-ink/60 leading-relaxed">
                  Thanks for helping shape the future of ROAMIES.<br />
                  We're building adventures around what real travelers actually want.
                </p>
                <p className="mt-3 font-display text-xl uppercase text-flame">Show up solo. Leave with a family.</p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button asChild data-testid="survey-explore-btn"
                    className="bg-flame hover:bg-flame/90 text-white rounded-none h-14 px-10 font-bold uppercase tracking-[0.2em] text-sm">
                    <Link to="/trips">Explore Roamies <ArrowRight className="w-4 h-4 ml-2" /></Link>
                  </Button>
                  <Button onClick={share} data-testid="survey-share-btn"
                    className="bg-transparent border-2 border-ink text-ink hover:bg-ink hover:text-cream rounded-none h-14 px-8 font-bold uppercase tracking-widest text-xs transition-colors">
                    <Share2 className="w-4 h-4 mr-2" /> Share My Travel Style
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
